<template>
  <div class="session-timeline">
    <div
      v-for="segment in segments"
      :key="segment.key"
      class="session-card"
      :class="`is-${segment.kind}`"
    >
      <div class="sc-header">
        <span
          class="sc-badge"
          :class="`is-${segment.kind}`"
        >
          {{ segment.label }}
        </span>
        <span
          v-if="segment.autoCreated"
          class="sc-badge-auto"
        >
          自動
        </span>
        <span class="sc-time">
          {{ hhmm(segment.startAt) }} → {{ hhmm(segment.releaseAt) }}（{{ segment.durationMinutes }}分）
        </span>
      </div>

      <div class="sc-body">
        <div class="sc-info">
          <div class="sc-name">
            {{ segment.vendibleName }}
          </div>
          <div class="sc-detail">
            単価 ¥{{ segment.unitPrice.toLocaleString() }} × {{ segment.size }}名
          </div>
        </div>
        <div class="sc-price">
          ¥{{ segment.subtotal.toLocaleString() }}
        </div>
      </div>

      <!-- 同一オーダー(orderGroupId)で同時に注文された指名・アイテム（1セット目・延長とも表示） -->
      <div
        v-if="companionsFor(segment).length > 0"
        class="sc-companions"
      >
        <div class="sc-companions-label">
          同時注文
        </div>
        <div
          v-for="companion in companionsFor(segment)"
          :key="`companion-${companion.id}`"
          class="sc-companion"
        >
          <span class="sc-companion-name">{{ companionLabel(companion) }}</span>
          <span class="sc-companion-price">¥{{ (Number(companion.attributes.sellingPrice) || 0).toLocaleString() }}</span>
        </div>
      </div>

      <!-- コースの時間変更 -->
      <div v-if="changingKey === segment.key">
        <!-- eslint-disable vue/v-on-event-hyphenation -->
        <!-- NOTE: 同一セグメントは全員同時刻のため、共通1組の入力で全コース履歴に適用する -->
        <persistence-report-card
          v-if="commonPersistenceReport"
          :value="commonPersistenceReport"
          class="my-4"
          @update:startAt="applyCommonStartAt($event)"
          @update:releaseAt="applyCommonReleaseAt($event)"
        />

        <!-- オーダー内の明細（vending_history単位）をチェックで個別解除できる -->
        <div
          v-if="editableLines.length > 0"
          class="sc-edit-lines"
        >
          <div class="sc-edit-lines-label">
            明細（チェックで解除）
          </div>
          <div
            v-for="line in editableLines"
            :key="`edit-line-${line.id}`"
            class="sc-edit-line"
            :class="{ 'is-removed': removalIds.includes(line.id) }"
          >
            <v-checkbox
              :input-value="removalIds.includes(line.id)"
              dense
              hide-details
              class="sc-edit-line-check ma-0 pa-0"
              :aria-label="`${line.label}を解除`"
              @change="toggleRemoval(line.id)"
            />
            <span class="sc-edit-line-name">{{ line.label }}</span>
            <span class="sc-edit-line-price">¥{{ line.price.toLocaleString() }}</span>
          </div>
          <p
            v-if="isRemovingAllCourses"
            class="sc-edit-lines-warn"
          >
            {{ `${currentClub.courseAlias}を全て解除する場合は「取り消す」を使用してください` }}
          </p>
        </div>

        <div class="w-full d-flex justify-space-around align-center px-2 pb-2">
          <v-btn
            color="primary"
            :ripple="false"
            :loading="isSubmitting"
            :disabled="!isValidPersistenceReportsToSubmit || isSubmitting || isRemovingAllCourses"
            @click="updateCourseHistories"
          >
            更新
          </v-btn>
          <v-btn
            :ripple="false"
            color="secondary"
            text
            small
            class="ml-4"
            @click="cancelChanging"
          >
            キャンセル
          </v-btn>
        </div>
      </div>

      <div
        v-else
        class="sc-actions"
      >
        <v-btn
          text
          small
          :ripple="false"
          class="edit-btn"
          aria-label="時間を編集"
          @click="changePersistenceReports(segment)"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiPencilOutline }}
          </v-icon>
          編集
        </v-btn>

        <v-btn
          v-if="cancellable && cancellable.key === segment.key"
          text
          small
          :ripple="false"
          class="cancel-btn"
          aria-label="延長を取り消す"
          @click="confirmCancel(segment)"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiClose }}
          </v-icon>
          取り消す
        </v-btn>
      </div>
    </div>

    <p
      v-if="segments.length === 0"
      class="ma-0 pa-0 text-sm text--secondary"
    >
      ...
    </p>

    <!-- 取り消し確認ダイアログ -->
    <v-dialog
      v-model="showCancelDialog"
      max-width="360"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          延長の取り消し
        </v-card-title>
        <v-card-text>
          <template v-if="cancelTarget">
            <p class="mb-2">
              {{ `${cancelTarget.label}を取り消しますか？以下の明細が削除されます。` }}
            </p>
            <div class="cancel-dialog-items">
              <div class="cancel-dialog-item">
                <span>{{ cancelTarget.vendibleName }} × {{ cancelTarget.size }}</span>
                <span>¥{{ cancelTarget.subtotal.toLocaleString() }}</span>
              </div>
              <div
                v-for="companion in cancelCompanions"
                :key="`cancel-companion-${companion.id}`"
                class="cancel-dialog-item"
              >
                <span>{{ companionLabel(companion) }}</span>
                <span>¥{{ (Number(companion.attributes.sellingPrice) || 0).toLocaleString() }}</span>
              </div>
            </div>
            <p
              v-if="cancelCompanions.length > 0"
              class="mt-2 mb-0 text-caption text--secondary"
            >
              同時に注文された明細もまとめて取り消されます。
            </p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :ripple="false"
            @click="showCancelDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            text
            :ripple="false"
            :loading="isCancelling"
            class="cancel-confirm-btn"
            @click="executeCancel"
          >
            取り消す
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  inject,
  ref,
  computed,
  getCurrentInstance,
  onUnmounted,
} from '@vue/composition-api'
import { each, every } from 'lodash'
import { mdiPencilOutline, mdiClose } from '@mdi/js'
import useCurrentData from '@/views/composable/useCurrentData'
import useDateFormat from '@/views/composable/useDateFormat'
import usePersistenceReport from '@/views/composable/usePersistenceReport'
import {
  groupCourseHistories,
  buildSessionSegments,
  cancellableSegment,
  companionLabel,
  collectCompanions,
  buildDestroyPayload,
} from '@/views/composable/useCourseSession'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import PersistenceReportCard from '@/views/components/table/PersistenceReportCard.vue'

export default {
  components: {
    PersistenceReportCard,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const vendingHistories = inject('vendingHistories', ref([]))

    const courseHistoriesTemp = ref([])
    const commonPersistenceReport = ref(null)
    const changingKey = ref(null)
    const isSubmitting = ref(false)
    const showCancelDialog = ref(false)
    const cancelTarget = ref(null)
    const isCancelling = ref(false)

    const { currentClub } = useCurrentData()
    const { hhmm } = useDateFormat()
    const {
      isValid: isValidPersistenceReport,
      buildPersistenceReportTemp,
    } = usePersistenceReport()

    const segments = computed(() => {
      return buildSessionSegments(groupCourseHistories(vendingHistories.value, hhmm))
    })

    const cancellable = computed(() => cancellableSegment(segments.value))

    // NOTE: 同一オーダーで同時に注文された指名・アイテム（旧データはorderGroupId無しで常に空）
    const companionsFor = segment => collectCompanions(vendingHistories.value, segment.orderGroupId)

    const cancelCompanions = computed(() => {
      if (!cancelTarget.value) return []

      return companionsFor(cancelTarget.value)
    })

    // 編集モードで解除(_destroy)対象に選択された明細のid
    const removalIds = ref([])

    const editingSegment = computed(() => {
      return segments.value.find(segment => segment.key === changingKey.value) || null
    })

    // 編集モードに出す明細一覧: セグメントのコース行(1名分ずつ) + 同一オーダーの指名・アイテム
    const editableLines = computed(() => {
      const segment = editingSegment.value
      if (!segment) return []

      const courseLines = segment.actualData.map(vendingHistory => ({
        id: vendingHistory.id,
        label: `${segment.vendibleName}（1名分）`,
        price: Number(vendingHistory.attributes?.sellingPrice) || 0,
        isCourse: true,
      }))
      const companionLines = companionsFor(segment).map(vendingHistory => ({
        id: vendingHistory.id,
        label: companionLabel(vendingHistory),
        price: Number(vendingHistory.attributes?.sellingPrice) || 0,
        isCourse: false,
      }))

      return [...courseLines, ...companionLines]
    })

    // コース行を全て解除するのは「取り消す」の役割（セグメントごと消える操作を編集に混ぜない）
    const isRemovingAllCourses = computed(() => {
      const segment = editingSegment.value
      if (!segment) return false

      return segment.actualData.every(vendingHistory => removalIds.value.includes(vendingHistory.id))
    })

    const toggleRemoval = id => {
      if (removalIds.value.includes(id)) {
        removalIds.value = removalIds.value.filter(removalId => removalId !== id)
      } else {
        removalIds.value = [...removalIds.value, id]
      }
    }

    const isValidPersistenceReportsToSubmit = computed(() => {
      return every(courseHistoriesTemp.value, courseHistoryTemp => isValidPersistenceReport(courseHistoryTemp.persistenceReportAttributes))
    })

    const cancelChanging = () => {
      changingKey.value = null
      courseHistoriesTemp.value = []
      commonPersistenceReport.value = null
      removalIds.value = []
    }

    // NOTE: 共通入力の時刻をセグメント内の全コース履歴に反映する
    const applyCommonStartAt = value => {
      if (commonPersistenceReport.value) commonPersistenceReport.value.startAt = value
      each(courseHistoriesTemp.value, courseHistoryTemp => {
        // eslint-disable-next-line no-param-reassign
        courseHistoryTemp.persistenceReportAttributes.startAt = value
      })
    }

    const applyCommonReleaseAt = value => {
      if (commonPersistenceReport.value) commonPersistenceReport.value.releaseAt = value
      each(courseHistoriesTemp.value, courseHistoryTemp => {
        // eslint-disable-next-line no-param-reassign
        courseHistoryTemp.persistenceReportAttributes.releaseAt = value
      })
    }

    const updateCourseHistories = async () => {
      isSubmitting.value = true

      // 解除対象は_destroyで送り、時間更新は解除されない行にだけ適用する
      const timeUpdates = courseHistoriesTemp.value.filter(courseHistoryTemp => !removalIds.value.includes(courseHistoryTemp.id))
      const destroys = removalIds.value.map(id => ({ id, _destroy: true }))

      try {
        const res = await VendingHistoryApi.updateVendingHistories({
          tableId: props.tableId,
          vendingHistories: [...timeUpdates, ...destroys],
        })

        if (res?.data) {
          emit('updated:vending-histories', res.data.vendingHistories.data)
          emit('updated:table', res.data.table.data)

          vm.$toast.success(`${currentClub.value.courseAlias}の時間を更新しました`)
        } else {
          vm.$toast.error(`${currentClub.value.courseAlias}の時間の更新に失敗しました`)
        }
      } catch (_e) {
        vm.$toast.error(`${currentClub.value.courseAlias}の時間の更新に失敗しました`)
      } finally {
        isSubmitting.value = false
        cancelChanging()
      }
    }

    // NOTE: 制限時間を表現するための持続レポート（PersistenceReport）を変更する
    const changePersistenceReports = segment => {
      courseHistoriesTemp.value = []
      removalIds.value = []
      changingKey.value = segment.key

      each(segment.actualData, courseHistory => {
        const courseHistoryTemp = { id: courseHistory.id }

        const persistenceReport = courseHistory.attributes.persistenceReport.data

        const {
          vendingHistoryId,
          startAt,
          releaseAt,
        } = persistenceReport.attributes

        const persistenceReportTemp = buildPersistenceReportTemp({
          id: persistenceReport.id,
          vendingHistoryId,
          startAt,
          releaseAt,
        })

        courseHistoryTemp.persistenceReportAttributes = persistenceReportTemp
        courseHistoriesTemp.value.push(courseHistoryTemp)
      })

      // NOTE: 共通の時刻入力（セグメントは全員同時刻なので先頭値で初期化）
      const first = courseHistoriesTemp.value[0]?.persistenceReportAttributes
      commonPersistenceReport.value = first
        ? { startAt: first.startAt, releaseAt: first.releaseAt }
        : null
    }

    const confirmCancel = segment => {
      cancelTarget.value = segment
      showCancelDialog.value = true
    }

    const executeCancel = async () => {
      if (!cancelTarget.value) return

      isCancelling.value = true

      try {
        const res = await VendingHistoryApi.updateVendingHistories({
          tableId: props.tableId,
          // 同一オーダーの指名・アイテムもまとめて取り消す（消し忘れ防止）
          vendingHistories: buildDestroyPayload(cancelTarget.value, cancelCompanions.value),
        })

        if (res?.data) {
          emit('updated:vending-histories', res.data.vendingHistories.data)
          emit('updated:table', res.data.table.data)

          vm.$toast.success('延長を取り消しました')
        } else {
          vm.$toast.error('延長の取り消しに失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('延長の取り消しに失敗しました')
      } finally {
        isCancelling.value = false
        showCancelDialog.value = false
        cancelTarget.value = null
      }
    }

    onUnmounted(() => cancelChanging())

    return {
      segments,
      cancellable,
      companionsFor,
      companionLabel,
      cancelCompanions,
      removalIds,
      editableLines,
      isRemovingAllCourses,
      toggleRemoval,
      currentClub,
      courseHistoriesTemp,
      commonPersistenceReport,
      changingKey,
      isSubmitting,
      showCancelDialog,
      cancelTarget,
      isCancelling,

      isValidPersistenceReportsToSubmit,

      hhmm,
      changePersistenceReports,
      applyCommonStartAt,
      applyCommonReleaseAt,
      cancelChanging,
      updateCourseHistories,
      confirmCancel,
      executeCancel,

      icons: {
        mdiPencilOutline,
        mdiClose,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.session-card {
  background: var(--overview-surface-2, #18181f);
  border: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;

  &.is-extension {
    border-color: var(--overview-accent-soft, rgba(139, 92, 246, 0.25));
  }
}

.sc-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
}

.sc-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;

  &.is-set {
    background: var(--overview-emerald-soft, rgba(52, 211, 153, 0.14));
    color: var(--overview-emerald, #34d399);
  }

  &.is-extension {
    background: var(--overview-accent-softer, rgba(139, 92, 246, 0.14));
    color: var(--overview-accent, #8b5cf6);
  }
}

.sc-badge-auto {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--overview-amber-soft, rgba(251, 191, 36, 0.16));
  color: var(--overview-amber, #f59e0b);
}

.sc-time {
  margin-left: auto;
  font-size: 13px;
  opacity: 0.65;
}

.sc-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sc-name {
  font-size: 15px;
  font-weight: 600;
}

.sc-detail {
  font-size: 13px;
  opacity: 0.6;
}

.sc-price {
  font-size: 17px;
  font-weight: 800;
}

// 同一オーダーで同時に注文された明細（延長カード内の小さな内訳）
.sc-companions {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed var(--overview-border, rgba(255, 255, 255, 0.07));
}

.sc-companions-label {
  font-size: 12px;
  color: var(--overview-text-3, #6a6a80);
  margin-bottom: 2px;
}

.sc-companion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  padding: 2px 0;
}

.cancel-dialog-items {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 8px 12px;
}

// 編集モードの明細一覧（チェックで個別解除）
.sc-edit-lines {
  margin: 0 8px 8px;
  padding: 8px 10px;
  border: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
  border-radius: 8px;
}

.sc-edit-lines-label {
  font-size: 12px;
  color: var(--overview-text-3, #6a6a80);
  margin-bottom: 4px;
}

.sc-edit-line {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  padding: 2px 0;

  &.is-removed {
    opacity: 0.5;

    .sc-edit-line-name,
    .sc-edit-line-price {
      text-decoration: line-through;
    }
  }
}

.sc-edit-line-name {
  flex: 1;
  min-width: 0;
}

.sc-edit-lines-warn {
  font-size: 12px;
  color: var(--overview-rose, #fb7185);
  margin: 4px 0 0;
}

.cancel-dialog-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding: 2px 0;
}

.sc-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.cancel-btn {
  color: var(--overview-rose, #fb7185) !important;
}
</style>
